import { Link } from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { useFormatAgreementNumber } from "@/entities/agreement"
import { useFormatSupplyPointMarketName } from "@/entities/supplyPoint"
import { GlobalBanner } from "@/features/banner"
import { NextIntlRichText } from "@/features/translations"
import { type FragmentOf, graphql, readFragment } from "@/graphql"
import { dashboardRoutesPaths } from "@/routes/dashboard"

const graphQLFragments = {
    agreement: graphql(
        `
            fragment AgreementWithInvalidPaymentInstructionBanner_Agreement on AgreementType {
                id
                supplyPoint {
                    meterPoint {
                        propertyId
                    }
                    ...useFormatSupplyPointMarketName_SupplyPoint
                }
                ...useFormatAgreementNumber_Agreement
            }
        `,
        [
            useFormatAgreementNumber.fragments.agreement,
            useFormatSupplyPointMarketName.fragments.supplyPoint,
        ]
    ),
}

type AgreementWithInvalidPaymentInstructionBannerProps = {
    accountNumber: string
    agreement: FragmentOf<typeof graphQLFragments.agreement>
}

export function AgreementWithInvalidPaymentInstructionBanner(
    props: AgreementWithInvalidPaymentInstructionBannerProps
) {
    const agreement = readFragment(graphQLFragments.agreement, props.agreement)

    const t = useTranslations("AgreementWithInvalidPaymentInstructionBanner")
    const formatAgreementNumber = useFormatAgreementNumber()

    if (!agreement.id || !agreement.supplyPoint?.meterPoint?.propertyId) {
        return null
    }

    const agreementId = agreement.id
    const propertyId = agreement.supplyPoint.meterPoint.propertyId

    const formattedAgreementNumber = formatAgreementNumber(agreement)

    return (
        <GlobalBanner>
            <NextIntlRichText>
                {(tags) =>
                    t.rich("content", {
                        ...tags,
                        agreementNumber: formattedAgreementNumber,
                        Link: (children: React.ReactNode) => (
                            <Link
                                href={dashboardRoutesPaths.bankingPaymentDetails.resolve({
                                    pathParams: {
                                        accountNumber: props.accountNumber,
                                        propertyId,
                                        agreementId: agreementId.toString(),
                                    },
                                })}
                            >
                                {children}
                            </Link>
                        ),
                    })
                }
            </NextIntlRichText>
        </GlobalBanner>
    )
}

AgreementWithInvalidPaymentInstructionBanner.messages = [
    "AgreementWithInvalidPaymentInstructionBanner",
    ...useFormatAgreementNumber.messages,
    ...useFormatSupplyPointMarketName.messages,
] as const

AgreementWithInvalidPaymentInstructionBanner.fragments = graphQLFragments
